import React from "react";
import { useFormContext } from "react-hook-form";
import { FTPAutoComplete, FTPInput, FTPRadioGroup } from "../../components-new";
type CustomFieldType = "text" | "number" | "date" | "textarea" | "select" | "radio" | "checkbox";

type CustomFieldSection = "subject" | "request";

type CustomFieldConfig = {
    id: string;
    label: string;
    type: CustomFieldType;
    section: CustomFieldSection;
    visible: boolean;
    required: boolean;
    placeholder?: string;
    options?: string[];
};
type Props = {
    fields?: CustomFieldConfig[];
    storagePath?: "subject" | "request";
};

const getFieldName = (storagePath: "subject" | "request", fieldId: string) =>
    storagePath === "subject" ? `customFields.${fieldId}` : `more.customFields.${fieldId}`;

const getFieldOptions = (field: CustomFieldConfig) =>
    (field.options || []).map((option) => ({
        label: option,
        value: option,
    }));

const ServiceRequestCustomFields = ({ fields = [], storagePath }: Props) => {
    const { register, formState } = useFormContext();

    const visibleFields = fields.filter((field) => field.visible !== false);

    if (!storagePath || visibleFields.length === 0) {
        return null;
    }

    return (
        <>
            {visibleFields.map((field) => {
                const name = getFieldName(storagePath, field.id);
                const label = `${field.label}${field.required ? " *" : ""}`;
                const error =
                    storagePath === "subject"
                        ? (formState.errors as any)?.customFields?.[field.id]
                        : (formState.errors as any)?.more?.customFields?.[field.id];

                if (field.type === "select") {
                    return (
                        <FTPAutoComplete
                            key={field.id}
                            className="col-span-1"
                            name={name}
                            label={label}
                            data={getFieldOptions(field)}
                            lookupKey="label"
                            placeholder={field.placeholder || "Select an option"}
                            multiple={false}
                            addNewOption={false}
                        />
                    );
                }

                if (field.type === "radio") {
                    return (
                        <div key={field.id} className="col-span-1 min-w-0">
                            <FTPRadioGroup name={name} label={label} options={getFieldOptions(field)} />
                        </div>
                    );
                }

                if (field.type === "checkbox") {
                    return (
                        <div key={field.id} className="col-span-1 flex items-center gap-2 pt-2">
                            <input
                                id={name}
                                type="checkbox"
                                {...register(name)}
                                className="h-4 w-4 cursor-pointer accent-blue-600"
                            />
                            <label htmlFor={name} className="cursor-pointer text-sm text-gray-700">
                                {field.label}
                                {field.required && <span className="ml-1 text-red-500">*</span>}
                            </label>
                            {error?.message && <p className="text-xs text-red-500">{error.message}</p>}
                        </div>
                    );
                }

                return (
                    <FTPInput
                        key={field.id}
                        name={name}
                        label={label}
                        placeholder={field.placeholder}
                        type={field.type === "number" ? "number" : field.type === "date" ? "date" : undefined}
                        numericOnly={field.type === "number"}
                        multiline={field.type === "textarea"}
                        className={field.type === "textarea" ? "col-span-1 md:col-span-2" : "col-span-1"}
                        inputProps={
                            field.type === "date"
                                ? {
                                      max: new Date().toISOString().split("T")[0],
                                  }
                                : undefined
                        }
                    />
                );
            })}
        </>
    );
};

export default ServiceRequestCustomFields;
export { getFieldName };
